import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import ActionMenu from '../components/common/ActionMenu';
import { Pencil, Trash2 } from 'lucide-react';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Editor } from '@tinymce/tinymce-react';
import './AdminBlog.css';

/*
 * TinyMCE initialisation (self-hosted build in `public/tinymce`).
 *
 * Kept at module scope so the config object is created once instead of on every
 * keystroke. `license_key: 'gpl'` is mandatory for self-hosted TinyMCE 8 and the
 * `plugins` list only names plugins that actually ship in `public/tinymce/plugins`.
 *
 * The toolbar follows the familiar Word-ribbon grouping: history · fonts and
 * block formats · character formatting · alignment and indentation · lists ·
 * insert (link/image/media/table/date/char) · cleanup · view and utilities.
 */
const TINYMCE_INIT = {
  license_key: 'gpl',
  height: 520,
  menubar: 'file edit view insert format table tools',
  plugins: 'advlist anchor autolink charmap code codesample directionality fullscreen help image insertdatetime link lists media pagebreak preview quickbars save searchreplace table visualblocks visualchars wordcount',
  toolbar: [
    'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough forecolor backcolor | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | link image media table | charmap codesample hr pagebreak insertdatetime | removeformat searchreplace | fullscreen preview code visualblocks wordcount',
  ],
  toolbar_mode: 'sliding',
  branding: false,
  promotion: false,
  statusbar: true,
  resize: true,
  // Inline image uploads reuse the existing POST /blog/upload-image endpoint
  // (multipart field `image`, bearer token added by the `api` axios instance).
  images_upload_handler: (blobInfo) => new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('image', blobInfo.blob(), blobInfo.filename());
    api.post('/blog/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => resolve(res.data.url || res.data.fullUrl))
      .catch(() => reject('Image upload failed'));
  }),
  image_uploadtab: true,
  automatic_uploads: true,
  file_picker_types: 'image',
  content_style: `/* typography mirroring the public article styles */
    body { font-family: Inter, ui-sans-serif, system-ui, sans-serif; font-size: 16px; line-height: 1.75; color: #334155; padding: 12px 16px; }
    h1,h2,h3,h4 { color: #0f172a; line-height: 1.25; }
    a { color: #1e3a8a; }
    blockquote { border-left: 4px solid #f9b424; background: #fffaeb; margin: 1.25em 0; padding: 0.75em 1em; font-style: italic; }
    img { max-width: 100%; height: auto; border-radius: 8px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #e2e8f0; padding: 8px 10px; }
    th { background: #f8fafc; }
    pre { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; }`,
  skin: 'oxide',
  content_css: 'default'
};

const AdminBlog = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category_id: '',
    status: 'published',
    show_on_homepage: false,
    featured_image: null,
    featured_image_url: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [useImageUrl, setUseImageUrl] = useState(false);

  const canManage = user?.role === 'admin' || user?.role === 'editor';

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    fetchComments();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/blog/admin/posts');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/blog/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get('/blog/admin/comments');
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, featured_image: file, featured_image_url: '' });
      setImagePreview(URL.createObjectURL(file));
      setUseImageUrl(false);
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData({ ...formData, featured_image_url: url, featured_image: null });
    if (url) {
      setImagePreview(url);
    } else {
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = new FormData();
    submitData.append('title', formData.title);
    submitData.append('content', formData.content);
    submitData.append('excerpt', formData.excerpt);
    submitData.append('category_id', formData.category_id);
    submitData.append('status', formData.status);
    submitData.append('show_on_homepage', formData.show_on_homepage);

    if (useImageUrl && formData.featured_image_url) {
      submitData.append('featured_image_url', formData.featured_image_url);
    } else if (formData.featured_image) {
      submitData.append('featured_image', formData.featured_image);
    }

    setLoading(true);
    try {
      if (editingPost) {
        await api.put(`/blog/admin/posts/${editingPost.id}`, submitData);
        toast.success('Post updated successfully');
      } else {
        await api.post('/blog/admin/posts', submitData);
        toast.success('Post created successfully');
      }
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(error.response?.data?.message || 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      category_id: post.category_id || '',
      status: post.status,
      show_on_homepage: post.show_on_homepage,
      featured_image: null,
      featured_image_url: post.featured_image || ''
    });

    if (post.featured_image) {
      const fullUrl = post.featured_image;
      setImagePreview(fullUrl);
      setUseImageUrl(fullUrl.startsWith('http'));
    }
    setShowForm(true);
    setActiveDropdown(null);
  };

  const handleDelete = async (id) => {
    setActiveDropdown(null);
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await api.delete(`/blog/admin/posts/${id}`);
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleApproveComment = async (id) => {
    try {
      await api.put(`/blog/admin/comments/${id}/approve`);
      toast.success('Comment approved successfully');
      fetchComments();
    } catch (error) {
      console.error('Error approving comment:', error);
      toast.error('Failed to approve comment');
    }
  };

  const handleDeleteComment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await api.delete(`/blog/admin/comments/${id}`);
      toast.success('Comment deleted successfully');
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      category_id: '',
      status: 'published',
      show_on_homepage: false,
      featured_image: null,
      featured_image_url: ''
    });
    setImagePreview(null);
    setUseImageUrl(false);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Actions Menu Component using React Portal
  const ActionsMenu = ({ postId, onEdit, onDelete }) => {
    if (activeDropdown !== postId) return null;

    return createPortal(
      <div
        ref={dropdownRef}
        className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 min-w-40"
        style={{
          top: `${window.event?.clientY || 0}px`,
          left: `${window.event?.clientX || 0}px`,
        }}
      >
        <button
          onClick={onEdit}
          className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
        >
          <span>✏️</span> Edit
        </button>
        <button
          onClick={onDelete}
          className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
        >
          <span>🗑️</span> Delete
        </button>
      </div>,
      document.body
    );
  };

  const handleThreeDotsClick = (e, postId) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === postId ? null : postId);
  };

  if (!canManage) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="text-center py-12">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Access Denied</h3>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Blog & News Management</h1>
          <p className="text-gray-600 mt-1">Manage posts, categories, and comments</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Write New Post'}
        </Button>
      </div>

      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('posts')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'posts' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Posts ({posts.length})
        </button>
        <button
          onClick={() => setActiveTab('comments')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'comments' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Comments ({comments.filter(c => !c.is_approved).length} pending)
        </button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">{editingPost ? 'Edit Post' : 'Create New Post'}</h2>
          <form onSubmit={handleSubmit}>
            <Input
              label="Title *"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
            />

            <div className="grid grid-cols-2 gap-4 my-4">
              <select
                className="input-field"
                value={formData.category_id}
                onChange={(e) => setFormData({...formData, category_id: e.target.value})}
              >
                <option value="">Select Category</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
              <select
                className="input-field"
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <label className="flex items-center gap-2 my-2">
              <input
                type="checkbox"
                checked={formData.show_on_homepage}
                onChange={(e) => setFormData({...formData, show_on_homepage: e.target.checked})}
              />
              Show on Homepage
            </label>

            <textarea
              className="input-field my-2"
              rows="3"
              placeholder="Excerpt (brief summary of the post)"
              value={formData.excerpt}
              onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
            />

            <div className="my-4">
              <label
                className="block text-sm font-medium text-gray-700 mb-2"
                htmlFor="blog-content-editor"
              >
                Content *
              </label>
              <div className="blog-editor-shell">
                <Editor
                  id="blog-content-editor"
                  tinymceScriptSrc="/tinymce/tinymce.min.js"
                  licenseKey="gpl"
                  value={formData.content}
                  onEditorChange={(html) => setFormData((prev) => ({ ...prev, content: html }))}
                  init={TINYMCE_INIT}
                />
              </div>
            </div>

            <div className="my-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image</label>
              <div className="flex gap-4 mb-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={!useImageUrl}
                    onChange={() => setUseImageUrl(false)}
                  />
                  Upload File
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={useImageUrl}
                    onChange={() => setUseImageUrl(true)}
                  />
                  Use Image URL
                </label>
              </div>

              {!useImageUrl ? (
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
                />
              ) : (
                <input
                  type="text"
                  placeholder="/images/blogs/your-image.jpg"
                  value={formData.featured_image_url}
                  onChange={handleImageUrlChange}
                  className="input-field"
                />
              )}

              {imagePreview && (
                <div className="mt-4">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-48 h-48 object-cover rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/200?text=Invalid+URL';
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Preview</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-4">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingPost ? 'Update Post' : 'Publish Post')}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {activeTab === 'posts' && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Homepage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {posts.map(post => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-left font-medium">{post.title}</td>
                  <td className="px-4 py-2 text-left">{post.category_name || '-'}</td>
                  <td className="px-4 py-2 text-left">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(post.status)}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-left">{post.show_on_homepage ? '✅' : '❌'}</td>
                  <td className="px-4 py-2 text-left">{post.view_count || 0}</td>
                  <td className="px-4 py-2 text-left">{new Date(post.published_at || post.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-left">
                    <ActionMenu
                      label={`Actions for ${post.title || 'post'}`}
                      items={[
                        { label: 'Edit post', icon: Pencil, onClick: () => handleEdit(post) },
                        {
                          label: 'Delete post',
                          icon: Trash2,
                          variant: 'danger',
                          onClick: () => handleDelete(post.id),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'comments' && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Post</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {comments.map(comment => (
                <tr key={comment.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-left">{comment.post_title}</td>
                  <td className="px-4 py-2 text-left">{comment.name}</td>
                  <td className="px-4 py-2 text-left">{comment.email}</td>
                  <td className="px-4 py-2 text-left max-w-xs truncate">{comment.comment}</td>
                  <td className="px-4 py-2 text-left">
                    <span className={`px-2 py-1 text-xs rounded-full ${comment.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {comment.is_approved ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-left">
                    {!comment.is_approved && (
                      <button
                        onClick={() => handleApproveComment(comment.id)}
                        className="text-green-600 mr-3 hover:text-green-800 text-sm font-medium"
                      >
                        Approve
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
